import { getScreenNameAuthenticated } from "../../utils/auth/Auth-utils";
import { getAccessToken, procesarRespuesta, reemplazarUrlServicio } from "../../utils/funciones-services";
import { v4 as uuidv4 } from 'uuid';

const DOCUMENT_TYPE_CODE_DNI = '1';
const DOCUMENT_TYPE_CODE_CE = '3';
const DOCUMENT_TYPE_CODE_PASS = '4';

const DOCUMENT_TYPE_CODE_BY_ALIAS = {
    [DOCUMENT_TYPE_CODE_DNI]: DOCUMENT_TYPE_CODE_DNI,
    [DOCUMENT_TYPE_CODE_CE]: DOCUMENT_TYPE_CODE_CE,
    [DOCUMENT_TYPE_CODE_PASS]: DOCUMENT_TYPE_CODE_PASS,
    DNI: DOCUMENT_TYPE_CODE_DNI,
    CE: DOCUMENT_TYPE_CODE_CE,
    PASS: DOCUMENT_TYPE_CODE_PASS,
    PASAPORTE: DOCUMENT_TYPE_CODE_PASS
};

const DOCUMENT_NUMBER_LENGTH_BY_TYPE = {
    [DOCUMENT_TYPE_CODE_DNI]: 8,
    [DOCUMENT_TYPE_CODE_CE]: 9,
    [DOCUMENT_TYPE_CODE_PASS]: 14
};

const normalizeText = (value) => {
    if (value === null || value === undefined) {
        return null;
    }

    const normalizedValue = String(value).trim();
    if (normalizedValue === '') {
        return null;
    }

    return normalizedValue;
};

const extractDocumentTypeCandidates = (tipoDocumento) => {
    if (tipoDocumento === null || tipoDocumento === undefined) {
        return [];
    }

    if (typeof tipoDocumento !== 'object') {
        return [tipoDocumento];
    }

    const nestedDocumentType = tipoDocumento.tipoDocumento;
    return [
        tipoDocumento.codigo,
        tipoDocumento.descripcion,
        tipoDocumento.value,
        tipoDocumento.valor,
        nestedDocumentType,
        nestedDocumentType && nestedDocumentType.codigo,
        nestedDocumentType && nestedDocumentType.descripcion
    ];
};

const normalizeDocumentTypeCode = (tipoDocumento) => {
    const candidates = extractDocumentTypeCandidates(tipoDocumento);
    for (const candidate of candidates) {
        const normalizedCandidate = normalizeText(candidate);
        if (!normalizedCandidate) {
            continue;
        }

        const upperCandidate = normalizedCandidate.toUpperCase();
        if (DOCUMENT_TYPE_CODE_BY_ALIAS[upperCandidate]) {
            return DOCUMENT_TYPE_CODE_BY_ALIAS[upperCandidate];
        }
    }

    return null;
};

const normalizeDocumentNumber = (numeroDocumento, targetLength) => {
    if (numeroDocumento === null || numeroDocumento === undefined) {
        return numeroDocumento;
    }

    const normalizedNumber = String(numeroDocumento).trim();
    if (normalizedNumber === '') {
        return normalizedNumber;
    }

    if (!targetLength) {
        return normalizedNumber;
    }

    if (normalizedNumber.length > targetLength) {
        return normalizedNumber.slice(normalizedNumber.length - targetLength);
    }

    return normalizedNumber.padStart(targetLength, '0');
};

const normalizeDocumentTypeFallback = (tipoDocumento) => {
    if (tipoDocumento === null || tipoDocumento === undefined) {
        return tipoDocumento;
    }

    if (typeof tipoDocumento === 'string' || typeof tipoDocumento === 'number') {
        return String(tipoDocumento).trim();
    }

    return tipoDocumento;
};

const normalizeConsentPayload = (datos) => {
    if (!datos) {
        return datos;
    }

    const normalizedTypeCode = normalizeDocumentTypeCode(datos.tipoDocumento);
    const targetLength = normalizedTypeCode
        ? DOCUMENT_NUMBER_LENGTH_BY_TYPE[normalizedTypeCode]
        : null;
    const normalizedDocumentNumber = normalizeDocumentNumber(datos.numeroDocumento, targetLength);
    const normalizedSolicitudMultipleId = datos.idSolicitudMultiple === null || datos.idSolicitudMultiple === undefined
        ? datos.idSolicitudMultiple
        : String(datos.idSolicitudMultiple).trim();

    return {
        ...datos,
        tipoDocumento: normalizedTypeCode || normalizeDocumentTypeFallback(datos.tipoDocumento),
        numeroDocumento: normalizedDocumentNumber,
        idSolicitudMultiple: normalizedSolicitudMultipleId
    };
};

const getHeaders = (idIntermediario) => {
    const headers = {
        'Ocp-Apim-Subscription-Key' : process.env.REACT_APP_SUSCRIPTION_KEY,
        'Content-Type'              : 'application/json',
        'Authorization'             : 'Bearer ' + getAccessToken(),
        'Transaccion-Id'            : uuidv4(),
        'Aplicacion-Id'             : process.env.REACT_APP_APLICACION_ID,
        'Nombre-Aplicacion'         : process.env.REACT_APP_NOMBRE_APLICACION,
        'Usuario-Consumidor-Id'     : getScreenNameAuthenticated() || 'ANONIMO',
        'Nombre-Servicio-Consumidor' : process.env.REACT_APP_NOMBRE_SERVICIO_CONSUMIDOR || 'ms-ux-di-sdvd-solicituddigitalvida'
    };

    if (idIntermediario !== null && idIntermediario !== undefined && String(idIntermediario).trim() !== '') {
        headers.idIntermediario = String(idIntermediario).trim();
    }

    return new Headers(headers);
};

export const consultarConsentimiento = async (datos) => {
    const normalizedDatos = normalizeConsentPayload(datos);
    return procesarRespuesta({
        url: process.env.REACT_APP_WS_CONSULTAR_CONSENTIMIENTO,
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ datos: normalizedDatos })
    });
};

export const registrarConsentimiento = async (datos, idIntermediario) => {
    const normalizedDatos = normalizeConsentPayload(datos);
    return procesarRespuesta({
        url: process.env.REACT_APP_WS_REGISTRAR_CONSENTIMIENTO,
        method: 'POST',
        headers: getHeaders(idIntermediario),
        body: JSON.stringify({ datos: normalizedDatos })
    });
};

export const actualizarConsentimiento = async (idAcuerdoCliente, datos, idIntermediario) => {
    let atributos = [{ campo: 'idAcuerdoCliente', valor: idAcuerdoCliente }];
    const normalizedDatos = normalizeConsentPayload(datos);
    return procesarRespuesta({
        url: reemplazarUrlServicio(process.env.REACT_APP_WS_ACTUALIZAR_CONSENTIMIENTO, atributos),
        method: 'PATCH',
        headers: getHeaders(idIntermediario),
        body: JSON.stringify({ datos: normalizedDatos })
    });
};

export const obtenerDocumentosConsentimiento = async () => {
    return procesarRespuesta({
        url: process.env.REACT_APP_WS_GET_DOCUMENTOS_CONSENTIMIENTO,
        method: 'GET',
        headers: getHeaders()
    });
};
